// Unified certificate verification server functions. Thin wrapper: every
// helper is imported from a *.server module so server-fn splitting is safe.

import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import type { CertProvider, NormalizedCertResult } from "@/lib/grading/cert-verification";
import { CERTIFICATE_PROVIDERS, providerModes } from "@/lib/grading/cert-verification.server";
import {
  readCachedCert,
  writeCachedCert,
  auditCertLookup,
} from "@/lib/grading/cert-verification-service.server";
import { enforceRateLimit } from "@/lib/rate-limit.server";
import { emptyResult } from "@/lib/grading/cert-verification";
import { syncRegistryFromVerification } from "@/lib/registry/registry-sync.server";

export const verifyCertificate = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z
      .object({
        provider: z.enum(["psa", "bgs", "cgc", "sgc", "ace", "tag"]),
        cert_number: z.string().trim().min(1).max(32),
        force_refresh: z.boolean().optional(),
        listing_id: z.string().uuid().optional(),
      })
      .parse(d),
  )
  .handler(async ({ data, context }): Promise<NormalizedCertResult> => {
    const { userId } = context;
    const provider = data.provider as CertProvider;
    const cert = data.cert_number.trim();
    const started = Date.now();
    // Server-side rate limit. Surfaced as a normalised `rate_limited` result
    // (the transport already sets HTTP 429 + Retry-After) so the UI can show
    // useful copy instead of a generic error.
    try {
      await enforceRateLimit("grading-cert-verify", { userId, preset: "aiUser" });
    } catch {
      return emptyResult(provider, cert, "rate_limited", "none", "LOCAL_RATE_LIMIT", undefined, 60);
    }

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const admin = supabaseAdmin as any;

    if (!data.force_refresh) {
      const cached = await readCachedCert(admin, provider, cert);
      if (cached) {
        await auditCertLookup(admin, {
          user_id: userId,
          provider,
          cert_number: cached.certificateNumber,
          result: cached,
          duration_ms: Date.now() - started,
          cache_hit: true,
        });
        await syncRegistryFromVerification({
          result: cached,
          userId,
          listingId: data.listing_id ?? null,
          cacheHit: true,
          durationMs: Date.now() - started,
        });
        return cached;
      }
    }

    const result = await CERTIFICATE_PROVIDERS[provider].verify(cert);
    await writeCachedCert(admin, result);
    await auditCertLookup(admin, {
      user_id: userId,
      provider,
      cert_number: result.certificateNumber,
      result,
      duration_ms: Date.now() - started,
      cache_hit: false,
    });
    // Registry sync is best-effort: it must never fail a valid verification.
    try {
      await syncRegistryFromVerification({
        result,
        userId,
        listingId: data.listing_id ?? null,
        cacheHit: false,
        durationMs: Date.now() - started,
      });
    } catch {
      /* non-fatal */
    }
    // Never leak the upstream fingerprint/payload hash to the browser.
    return { ...result, fingerprint: null };
  });


export const getCertProviderModes = createServerFn({ method: "GET" }).handler(async () =>
  providerModes(),
);
